import type { useDialogManager } from '@up2promo-setup-frontend/core/hooks/use-dialog-manager';
import MessageModal from '@up2promo-setup-frontend/ui/components/modals/message.modal';
import { ProductsPublicApi } from '@up2stay/up2promo-api-client';
import type { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';

import { useAppState } from '../store/app-state.store';
import { useSetupStore } from '../store/setup.store';

interface UseHashCheckerOptions {
  hash: string | undefined;
  openDialog: ReturnType<typeof useDialogManager>['openDialog'];
  translations: {
    general: {
      loadingError: {
        title: string;
        description: string;
      };
    };
  };
  onHashSet?: () => void;
}

/**
 * Hook for checking hash and handling redirection.
 * Only runs once per hash change using ref guard.
 */
export function useHashChecker({
  hash,
  openDialog,
  translations,
  onHashSet,
}: UseHashCheckerOptions) {
  const setHash = useSetupStore((state) => state.setHash);
  const hashValue = useSetupStore((state) => state.hash);
  const hideSpinner = useAppState((state) => state.hideSpinner);
  const hasCheckedRef = useRef<string | null>(null);
  const isCheckingRef = useRef(false);

  useEffect(() => {
    // Redirect to home if no hash provided
    if (!hash) {
      window.location.href = 'https://www.up2promo.com';
      return;
    }

    // Guard to prevent multiple checks of the same hash
    if (hasCheckedRef.current === hash || isCheckingRef.current) {
      return;
    }

    // Check if hash already exists in store and matches
    if (hashValue === hash) {
      onHashSet?.();
      hideSpinner();
      hasCheckedRef.current = hash;
      return;
    }

    isCheckingRef.current = true;

    const checkHash = async () => {
      try {
        // Check whether the product exists
        const productResponse = await ProductsPublicApi.findOneByHashOnly({
          path: {
            hash,
          },
        });

        // Already set up (unowned): go to its URL. The public view carries
        // the URL only while unowned (DEV-753); the forwarder never sends a
        // configured claimed product here.
        const configuredUrl = productResponse.data?.targetUrl;
        if (configuredUrl) {
          window.location.href = configuredUrl;
          return;
        }

        // Hash not found - set it in store and continue setup flow
        setHash(hash);
        onHashSet?.();
        hideSpinner();
        hasCheckedRef.current = hash;
      } catch (error) {
        const axiosError = error as AxiosError;

        // Only show error for non-404 errors
        if (axiosError.response?.status !== 404) {
          void openDialog(MessageModal, {
            componentProps: {
              title: translations.general.loadingError.title,
              bodyText: translations.general.loadingError.description,
              isDestructive: true,
            },
          }).then(
            () => {
              window.location.reload();
            },
            () => {
              window.location.reload();
              return Promise.reject();
            },
          );
          hideSpinner();
          return;
        }

        // 404 is expected - hash doesn't exist, continue with setup
        setHash(hash);
        onHashSet?.();
        hideSpinner();
        hasCheckedRef.current = hash;
      } finally {
        isCheckingRef.current = false;
      }
    };

    void checkHash();
  }, [
    hash,
    hashValue,
    setHash,
    openDialog,
    translations.general.loadingError.title,
    translations.general.loadingError.description,
    hideSpinner,
    onHashSet,
  ]);
}
